"use client";

import React, { useState } from "react";
import { Users } from "lucide-react";
import { Button } from "../button.js";
import { CalendarEvent } from "../../types/calendar.js";
import { AttendeeDetailsModal } from "./AttendeeDetailsModal.js";
import { AttendeeList } from "./attendee/AttendeeList.js";
import { SectionHeader } from "./shared/SectionHeader.js";
import { useAttendeeData } from "../../hooks/useAttendeeData.js";

interface EventAttendeesTabProps {
  event: CalendarEvent;
  isPageView?: boolean;
  onComposeEmail?: (recipients: string[], subject?: string) => void;
}

export function EventAttendeesTab({
  event,
  isPageView = false,
  onComposeEmail,
}: EventAttendeesTabProps) {
  const [showAttendeeModal, setShowAttendeeModal] = useState(false);
  const { attendees, organizer, formatCount } = useAttendeeData(event);

  return (
    <div className="space-y-8">
      {/* Attendees Overview */}
      <section className="space-y-4">
        <SectionHeader title="Attendees Overview" color="slate" icon={Users} />
        <div className="flex items-center justify-between p-4 bg-card border border-gray-200 rounded-lg shadow-sm">
          <div className="flex items-center space-x-3">
            <Users className="h-6 w-6 text-slate-600" />
            <div>
              <h3 className="font-semibold text-lg text-slate-900">Attendees</h3>
              <p className="text-sm text-slate-700">
                {formatCount()}
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAttendeeModal(true)}
            className="flex items-center space-x-2"
          >
            <Users className="h-4 w-4" />
            <span>View All</span>
          </Button>
        </div>
      </section>

      {/* Standardized Attendees List */}
      <section className="space-y-4">
        <SectionHeader title="Attendees List" color="slate" />
        <AttendeeList
          attendees={attendees}
          organizer={organizer || undefined}
          onComposeEmail={onComposeEmail}
          maxVisible={5}
          showSummary={false}
          variant="detailed"
        />
      </section>

      {/* Attendee Details Modal */}
      <AttendeeDetailsModal
        attendees={attendees}
        organizer={organizer || undefined}
        isOpen={showAttendeeModal}
        onClose={() => setShowAttendeeModal(false)}
        eventTitle={event.title}
        onComposeEmail={onComposeEmail}
      />
    </div>
  );
}
